export function getEmailFromParams(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get('email');
  if (!raw) return null;
  // URLSearchParams decodes '+' into spaces; restore plus-addressing
  return raw.replace(/\s/g, '+');
}

/**
 * Validates if a string is a valid URL with strict checks
 * - Must have http or https protocol
 * - Must have a valid hostname (domain with TLD or localhost)
 * - Prevents invalid patterns like "https:;something" or "https://example:;"
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  try {
    const urlObj = new URL(trimmed);

    // Must have http or https protocol
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return false;
    }

    // Must have a hostname (domain)
    if (!urlObj.hostname || urlObj.hostname.length === 0) {
      return false;
    }

    // Hostname must contain at least one dot (for TLD) or be localhost
    // This prevents invalid hostnames like "https://example" or "https://example:"
    const hasValidHostname =
      urlObj.hostname === 'localhost' || urlObj.hostname.includes('.') || /^\[.*\]$/.test(urlObj.hostname); // IPv6 addresses

    if (!hasValidHostname) {
      return false;
    }

    // Check for common invalid patterns
    // Prevent things like "https:;something" or "https://example:;"
    // Semicolons can appear in query strings, so we check if it's in the hostname/port part
    const beforeQuery = trimmed.split('?')[0].split('#')[0];
    if (beforeQuery.includes(';')) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * An absolute https URL to an already-hosted asset — the shape an authored image or video
 * URL must have before it is rendered as an `img`/`video` `src`.
 *
 * An app-relative path is rejected on purpose: authored steps render in more than one app
 * on different origins, so '/images/step.png' resolves somewhere different depending on
 * where the reader met it. Mirrors the API's `validHostedUrl`, and is the render-time guard
 * for rows authored before the API enforced it.
 */
export function validHostedUrl(value: string): boolean {
  if (!value.startsWith('https://')) return false;
  try {
    return Boolean(new URL(value).hostname);
  } catch {
    return false;
  }
}
